// src/components/common/CustomSelect.jsx
import React from "react";
import Select, { components } from "react-select";
import { useNavigate } from "react-router-dom";
import { PlusCircle } from "lucide-react";

const customStyles = {
  control: (base) => ({
    ...base,
    borderColor: "var(--input-stroke)",
    backgroundColor: "var(--input-bg)",
    color: "var(--text-primary-light)",
    boxShadow: "none",
    minHeight: "40px",
    maxHeight: "none",
    ":hover": {
      borderColor: "var(--primary-500)",
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "var(--input-bg)",
    color: "var(--text-primary-light)",
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "250px",
    overflowY: "auto",
    paddingTop: "0",
    paddingBottom: "0",
  }),
  input: (base) => ({
    ...base,
    color: "var(--text-primary-light)",
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--text-secondary-light)",
  }),
  valueContainer: (base) => ({
    ...base,
    maxHeight: "80px",
    overflowY: "auto",
    flexWrap: "wrap",
    color: "var(--text-primary-light)",
    padding: "2px 8px",
  }),
  singleValue: (base) => ({
    ...base,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "var(--text-primary-light)",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--primary-600)"
      : "var(--input-bg)",
    color: state.isSelected
      ? "var(--white)"
      : "var(--text-primary-light)",
    cursor: "pointer",
    padding: "10px 12px",
    pointerEvents: "auto",
    "&:hover": {
      backgroundColor: "var(--primary-600)",
      color: "var(--white)",
    },
    ":active": {
      backgroundColor: "var(--primary-500)",
      color: "var(--white)",
    },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "var(--primary-100)",
    maxWidth: "100%",
    overflow: "hidden",
  }),
  multiValueLabel: (base) => ({
    ...base,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "var(--text-primary-light)",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "var(--text-primary-light)",
    ":hover": {
      backgroundColor: "var(--danger-500)",
      color: "var(--white)",
    },
  }),
  indicatorsContainer: (base) => ({
    ...base,
    color: "var(--text-secondary-light)",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "var(--text-secondary-light)",
    ":hover": {
      color: "var(--primary-600)",
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "var(--text-secondary-light)",
    ":hover": {
      color: "var(--danger-600)",
    },
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: "var(--input-stroke)",
  }),
};

const CustomOption = (props) => {
  const { data, innerRef, innerProps } = props;
  if (data.__isAddNew) {
    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{ padding: "10px", cursor: "pointer" }}
        className="text-primary-600"
      >
        <div className="d-flex justify-content-center align-content-center gap-2">
          <PlusCircle /> {data.label}
        </div>
      </div>
    );
  }
  return <components.Option {...props} />;
};

const CustomSelect = ({
  name,
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  addNewLabel = "Add New",
  addNewRoute = "",
  isMulti = false,
  isSearchable = true,
  isClearable = true,
  labelKey = "label",
  valueKey = "value",
  state = false,
  stateData = {},
  isLoading = false,
  noOptionsMessage,
  onMenuOpen, 
  onMenuClose, 
}) => {
  const navigate = useNavigate();
  
  const mappedOptions = options.map((item) => ({
    label: item[labelKey],
    value: item[valueKey],
    raw: item,
  }));

  const extendedOptions = [
    ...mappedOptions,
    ...(addNewRoute
      ? [{ label: addNewLabel, value: "__add_new__", __isAddNew: true }]
      : []),
  ];

  const handleChange = (selected) => {
    if (isMulti) {
      const isAddNewSelected = selected?.some(
        (item) => item.value === "__add_new__"
      );
      if (isAddNewSelected) {
        if (state) {
          navigate(addNewRoute, { state: { stateData } });
        } else {
          navigate(addNewRoute);
        }
      } else {
        onChange(selected);
      }
    } else {
      if (selected?.value === "__add_new__") {
        if (state) {
          navigate(addNewRoute, { state: { stateData } });
        } else {
          navigate(addNewRoute);
        }
      } else {
        onChange(selected);
      }
    }
  };

  const getMappedValue = () => {
    if (isMulti) {
      return value?.map((val) =>
        typeof val === "object" && val?.label && val?.value
          ? val
          : mappedOptions.find((opt) => opt.value === val)
      );
    } else {
      return typeof value === "object" && value?.label && value?.value
        ? value
        : mappedOptions.find((opt) => opt.value === value) || null;
    }
  };

  return (
    <Select
      name={name}
      options={extendedOptions}
      value={getMappedValue()}
      onChange={handleChange}
      isSearchable={isSearchable}
      isClearable={isClearable}
      isMulti={isMulti}
      placeholder={isLoading ? "Loading..." : placeholder}
      components={{ Option: CustomOption }}
      classNamePrefix="react-select"
      styles={customStyles}
      isLoading={isLoading}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      maxMenuHeight={250}
    />
  );
};

export default CustomSelect;